import React from "react";
import { trodoAnalyticsTheme as theme } from "features/analytics/studio/theme";
import { formatNumber } from "shared/utils/numberFormatter";
import {
  tableHeaderRowClass,
  tableHeaderCellClass,
  tableDataCellClass,
} from "features/activity/styles/dataTableStyles";
import { formatDurationSeconds } from "features/analytics/studio/utils/durationFormat";
import { getSeriesColor } from "features/analytics/studio/utils/chartColors";

/** The Traces table chrome (dataTableStyles): one bordered card, hairline rows, no header fill. */
const tableContainerClass =
  "mt-4 overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface";
const tableStaticRowClass = "h-8 border-b border-[color:var(--border-subtle)] last:border-b-0";

const CHART_COLORS = [
  theme.accentChart,
  theme.primaryLight,
  "#8b5cf6",
  "#10b981",
  "var(--feedback-warning)",
  "#ec4899",
  "#6366f1",
  "#14b8a6",
];

function primaryYFromPoint(point, measuring) {
  if (!point) return null;
  if (measuring === "conversion_rate") {
    return point.conversion != null ? Number(point.conversion) : null;
  }
  if (measuring === "time_to_convert") {
    const v = point.value ?? point.conversion;
    return v != null && v !== "" ? Number(v) : null;
  }
  const v = point.value ?? point.conversion;
  return v != null && v !== "" ? Number(v) : null;
}

function formatCell(value, measuring) {
  if (value == null || Number.isNaN(value)) return "—";
  switch (measuring) {
    case "conversion_rate":
      return `${Number(value).toFixed(1)}%`;
    case "time_to_convert":
      return formatDurationSeconds(value);
    case "total_conversions":
    case "total_sessions":
    case "unique_users":
    case "property_sum":
      return formatNumber(value);
    default:
      return formatNumber(value);
  }
}

function formatDateHeader(dateStr, timeGranularity = "day") {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return String(dateStr).slice(-5);
    if (timeGranularity === "month") {
      return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
    }
    if (timeGranularity === "hour") {
      return d.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    }
    const month = d.toLocaleDateString("en-US", { month: "short" });
    const day = d.getDate();
    return `${month} ${day}`;
  } catch {
    return String(dateStr).slice(-5);
  }
}

/**
 * Time-series table under funnel line chart (insights-style): one row per series,
 * columns = Average + one per bucket. Values match the line chart Y metric.
 */
export default function FunnelTimeSeriesTable({
  points = [],
  breakdownSegments = null,
  measuring = "conversion_rate",
  metricTitle = "Funnel",
  seriesColors,
  timeGranularity = "day",
}) {
  const hasBreakdown =
    Array.isArray(breakdownSegments) && breakdownSegments.length > 0;

  if (hasBreakdown) {
    const dateSet = new Set();
    breakdownSegments.forEach((seg) => {
      (seg.points || []).forEach((p) => {
        if (p.date) dateSet.add(p.date);
      });
    });
    const dates = Array.from(dateSet).sort((a, b) =>
      String(a).localeCompare(String(b))
    );

    const allSeries = breakdownSegments.map((seg, idx) => {
      const pts = seg.points || [];
      const yVals = dates.map((dt) => {
        const p = pts.find((x) => x.date === dt);
        return primaryYFromPoint(p, measuring);
      });
      const valid = yVals.filter((v) => v != null && !Number.isNaN(v));
      const avg =
        valid.length > 0
          ? valid.reduce((a, b) => a + b, 0) / valid.length
          : null;
      return {
        metricId: seg.segment || `seg-${idx}`,
        metricName: seg.segment || `Segment ${idx + 1}`,
        color: getSeriesColor(idx, seriesColors || CHART_COLORS),
        yVals,
        average: avg,
      };
    });

    return (
      <div className={tableContainerClass}>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className={tableHeaderRowClass}>
                <th
                  className={tableHeaderCellClass}
                >
                  Segment
                </th>
                <th
                  className={`${tableHeaderCellClass} text-right`}
                >
                  Average
                </th>
                {dates.map((date, index) => (
                  <th
                    key={index}
                    className={`${tableHeaderCellClass} text-right min-w-[72px]`}
                  >
                    {formatDateHeader(date, timeGranularity)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {allSeries.map((s) => (
                <tr key={s.metricId} className={tableStaticRowClass}>
                  <td className={tableDataCellClass}>
                    <div className="flex items-center gap-2">
                      <div
                        className="h-2.5 w-2.5 shrink-0 rounded"
                        style={{ backgroundColor: s.color }}
                      />
                      <span>{s.metricName}</span>
                    </div>
                  </td>
                  <td
                    className={`${tableDataCellClass} text-right tabular-nums`}
                  >
                    {s.average != null ? formatCell(s.average, measuring) : "—"}
                  </td>
                  {s.yVals.map((val, i) => (
                    <td
                      key={i}
                      className={`${tableDataCellClass} text-right tabular-nums`}
                    >
                      {val != null && !Number.isNaN(val)
                        ? formatCell(val, measuring)
                        : "—"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  const series = (points || []).map((p) => ({
    date: p.date,
    value: primaryYFromPoint(p, measuring),
  }));
  const yValues = series.map((x) => x.value).filter((v) => v != null && !Number.isNaN(v));
  const average =
    yValues.length > 0
      ? yValues.reduce((a, b) => a + b, 0) / yValues.length
      : null;
  const color =
    seriesColors && seriesColors.length > 0
      ? seriesColors[0]
      : theme.accentChart;

  return (
    <div className={tableContainerClass}>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className={tableHeaderRowClass}>
              <th
                className={tableHeaderCellClass}
              >
                Metric
              </th>
              <th
                className={`${tableHeaderCellClass} text-right`}
              >
                Average
              </th>
              {series.map((item, index) => (
                <th
                  key={index}
                  className={`${tableHeaderCellClass} text-right min-w-[72px]`}
                >
                  {formatDateHeader(item.date, timeGranularity)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className={tableStaticRowClass}>
              <td className={tableDataCellClass}>
                <div className="flex items-center gap-2">
                  <div
                    className="h-2.5 w-2.5 shrink-0 rounded"
                    style={{ backgroundColor: color }}
                  />
                  <span>{metricTitle || "Funnel"}</span>
                </div>
              </td>
              <td
                className={`${tableDataCellClass} text-right tabular-nums`}
              >
                {average != null ? formatCell(average, measuring) : "—"}
              </td>
              {series.map((item, index) => (
                <td
                  key={index}
                  className={`${tableDataCellClass} text-right tabular-nums`}
                >
                  {item.value != null && !Number.isNaN(item.value)
                    ? formatCell(item.value, measuring)
                    : "—"}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
